import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Node } from 'reactflow';
import BaseInstance from '../../../../Services/AxiosInstance/BaseInstance';
import { MODELS } from '../../../../Services/Endpoint';

interface ConditionalRouteConfigPanelProps {
  nodeId: string;
  nodeData: Node['data'];
  onSave: (nodeId: string, data: Node['data']) => void;
  onClose: () => void;
}

interface ProvidersResponse {
  [providerName: string]: string[];
}

const ConditionalRouteConfigPanel: React.FC<ConditionalRouteConfigPanelProps> = ({
  nodeId,
  nodeData,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(nodeData.label || '');
  const [provider, setProvider] = useState(nodeData.provider || '');
  const [model, setModel] = useState(nodeData.model || '');
  const [apiKey, setApiKey] = useState(nodeData.apiKey || '');
  const [condition, setCondition] = useState(nodeData.condition || '');
  const [providers, setProviders] = useState<ProvidersResponse>({});
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch providers from API
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(MODELS);
        setProviders(response.data);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        // Fallback to empty object if API fails
        setProviders({});
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  // Sync when a different Conditional Route node is selected while panel is open
  useEffect(() => {
    setName(nodeData.label || '');
    setProvider(nodeData.provider || '');
    setModel(nodeData.model || '');
    setApiKey(nodeData.apiKey || '');
    setCondition(nodeData.condition || '');
  }, [nodeId, nodeData]);

  // Update models when provider changes
  useEffect(() => {
    if (!provider) {
      setModels([]);
      return;
    }

    // Get models for the selected provider from the API data
    const providerModels = providers[provider] || [];
    setModels(providerModels);
  }, [provider, providers]);

  const handleSave = () => {
    const updatedData = {
      label: name,
      provider,
      model,
      apiKey,
      condition,
    };
    onSave(nodeId, updatedData);
  };

  return (
    <Paper
      sx={{
        width: 400,
        height: '100vh',
        p: 3,
        borderRadius: 0,
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        right: 0,
        zIndex: 1200,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Configure Conditional Route
        </Typography>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Configuration Form */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto' }}>
        {/* Node Name */}
        <TextField
          label="Node Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />

        {/* Provider Selection */}
        <FormControl fullWidth size="small">
          <InputLabel>Provider</InputLabel>
          <Select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              setModel(''); // Reset model when provider changes
            }}
            label="Provider"
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading providers...
              </MenuItem>
            ) : (
              Object.keys(providers).map((providerName) => (
                <MenuItem key={providerName} value={providerName}>
                  {providerName}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Model Selection */}
        <FormControl fullWidth size="small">
          <InputLabel>Model</InputLabel>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            label="Model"
            disabled={loading || !provider}
          >
            {models.map((modelName) => (
              <MenuItem key={modelName} value={modelName}>
                {modelName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* API Key */}
        <TextField
          label="API Key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          type="password"
          placeholder="Enter your API key..."
        />

        {/* Condition */}
        <TextField
          label="Condition (Natural Language)"
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          fullWidth
          multiline
          rows={6}
          variant="outlined"
          size="small"
          placeholder="Describe the condition in natural language. For example: 'If the text contains a question mark' or 'If the text length is greater than 100 characters'"
        />
      </Box>

      {/* Save Button */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={handleSave}
          variant="contained"
          fullWidth
          disabled={!name || !provider || !model || !apiKey}
        >
          Save Configuration
        </Button>
      </Box>
    </Paper>
  );
};

export default ConditionalRouteConfigPanel;